/**
 * Canvas Element Hooks
 *
 * Utilities for working with nested canvas elements.
 * Ported from the original MyVault application.
 */

import { useMemo } from "react";
import type { CanvasElement } from "../types";

/**
 * Filter and sort child elements of a parent container.
 *
 * @param elements - All canvas elements
 * @param parentId - ID of the parent container
 * @param panelId - Optional panel ID for Tabs/Accordion filtering
 * @returns Sorted array of child elements
 */
export function getChildElements(
  elements: CanvasElement[],
  parentId: string,
  panelId?: string,
): CanvasElement[] {
  return elements
    .filter((el) => {
      // Must be a child of this parent
      if (el.parentElementId !== parentId) return false;
      // If panelId specified, must match (for Tabs/Accordion)
      if (panelId && el.parentPanelId !== panelId) return false;
      return true;
    })
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
}

/**
 * Get root-level elements (no parent).
 *
 * @param elements - All canvas elements
 * @returns Elements without a parentElementId
 */
export function getRootElements(elements: CanvasElement[]): CanvasElement[] {
  return elements
    .filter((el) => !el.parentElementId)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
}

/**
 * Hook to get child elements of a parent container.
 * Memoized for performance.
 *
 * @param elements - All canvas elements
 * @param parentId - ID of the parent container
 * @param panelId - Optional panel ID for Tabs/Accordion
 * @returns Memoized sorted array of child elements
 */
export function useChildElements(
  elements: CanvasElement[],
  parentId: string,
  panelId?: string,
): CanvasElement[] {
  return useMemo(
    () => getChildElements(elements, parentId, panelId),
    [elements, parentId, panelId],
  );
}

/**
 * Hook to get root-level elements.
 * Memoized for performance.
 *
 * @param elements - All canvas elements
 * @returns Memoized array of root elements
 */
export function useRootElements(elements: CanvasElement[]): CanvasElement[] {
  return useMemo(() => getRootElements(elements), [elements]);
}

/**
 * Check if an element is a container type.
 *
 * @param element - Canvas element to check
 * @returns True if the element is a container
 */
export function isContainerElement(element: CanvasElement): boolean {
  return (
    element.type === "container" ||
    element.type === "card" ||
    element.type === "tabs" ||
    element.type === "accordion"
  );
}

/**
 * Check if dropping element A into element B would create a cycle.
 * Prevents dropping a container into itself or its descendants.
 *
 * @param elements - All canvas elements
 * @param draggedId - ID of element being dragged
 * @param targetId - ID of potential drop target
 * @returns True if the drop would create a cycle
 */
export function wouldCreateCycle(
  elements: CanvasElement[],
  draggedId: string,
  targetId: string,
): boolean {
  // Can't drop into self
  if (draggedId === targetId) return true;

  // Check if target is a descendant of dragged element
  const isDescendant = (parentId: string, checkId: string): boolean => {
    const children = getChildElements(elements, parentId);
    for (const child of children) {
      if (child.id === checkId) return true;
      if (isDescendant(child.id, checkId)) return true;
    }
    return false;
  };

  return isDescendant(draggedId, targetId);
}

/**
 * Calculate the next display order for a new child element.
 *
 * @param elements - All canvas elements
 * @param parentId - ID of the parent container (or undefined for root)
 * @param panelId - Optional panel ID
 * @returns Next display order number
 */
export function getNextDisplayOrder(
  elements: CanvasElement[],
  parentId?: string,
  panelId?: string,
): number {
  const siblings = parentId
    ? getChildElements(elements, parentId, panelId)
    : getRootElements(elements);

  if (siblings.length === 0) return 0;

  const maxOrder = Math.max(...siblings.map((el) => el.displayOrder || 0));
  return maxOrder + 1;
}
